import React from 'react';
import { Card, VStack } from '@adc/parallax-component-library';
import DynamicForm, {
  DynamicFormConfig,
  DynamicFormFieldType,
} from '../../form/DynamicForm';
import { Device, UpdateRequest } from '../../../models';
import * as Yup from 'yup';
import i18n from '../../../utilities/i18n';

type DeviceHeaderProps = {
  device: Device;
  resolvedHierarchyNames?: {
    organizationName?: string;
    studyName?: string;
    siteName?: string;
    subjectName?: string;
  };
};

type DeviceFormValues = Device &
  UpdateRequest & {
    newUpdateReason: string;
  };

/**
 * DeviceHeader component displays the header information for a specific device.
 * @param {Device} device - The device object containing details to display.
 * @param {Object} resolvedHierarchyNames - Optional resolved display names for hierarchy entities.
 * @returns {React.JSX.Element}
 */
export default function DeviceHeader({
  device,
  resolvedHierarchyNames,
}: DeviceHeaderProps) {
  const mapDeviceToFormValues = (device: Device): DeviceFormValues => ({
    ...device,
    organizationKey:
      resolvedHierarchyNames?.organizationName || device.organizationKey,
    studyKey: resolvedHierarchyNames?.studyName || device.studyKey,
    siteKey: resolvedHierarchyNames?.siteName || device.siteKey,
    subjectKey: resolvedHierarchyNames?.subjectName || device.subjectKey,
    newUpdateReason: '',
  });

  const formConfig: DynamicFormConfig<DeviceFormValues> = {
    fields: [
      {
        name: 'appName',
        label: i18n.t('Page_Device.Form.appName'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'deviceId',
        label: i18n.t('Page_Device.Form.deviceId'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'organizationKey',
        label: i18n.t('Page_Device.Form.organization'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'studyKey',
        label: i18n.t('Page_Device.Form.study'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'siteKey',
        label: i18n.t('Page_Device.Form.site'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'subjectKey',
        label: i18n.t('Page_Device.Form.subject'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'bundleId',
        label: i18n.t('Page_Device.Form.bundleId'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'bundleVersion',
        label: i18n.t('Page_Device.Form.bundleVersion'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'timezone',
        label: i18n.t('Page_Device.Form.timezone'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'newUpdateReason',
        label: i18n.t('Page_Device.Form.newUpdateReason'),
        type: DynamicFormFieldType.TEXT,
        isEditable: true,
        isEditOnly: true,
      },
      {
        name: 'os',
        label: i18n.t('Page_Device.Form.os'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'osVersion',
        label: i18n.t('Page_Device.Form.osVersion'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'phoneModel',
        label: i18n.t('Page_Device.Form.phoneModel'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'phoneManufacturer',
        label: i18n.t('Page_Device.Form.phoneManufacturer'),
        type: DynamicFormFieldType.TEXT,
        isEditable: false,
      },
      {
        name: 'createdBy',
        label: i18n.t('Page_Device.Form.createdBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'createdOn',
        label: i18n.t('Page_Device.Form.createdOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updatedBy',
        label: i18n.t('Page_Device.Form.updatedBy'),
        type: DynamicFormFieldType.TEXT,
      },
      {
        name: 'updatedOn',
        label: i18n.t('Page_Device.Form.updatedOn'),
        type: DynamicFormFieldType.DATE,
      },
      {
        name: 'updateReason',
        label: i18n.t('Page_Device.Form.updateReason'),
        type: DynamicFormFieldType.TEXT,
      },
    ],
    validationSchema: {
      key: Yup.string(),
      newUpdateReason: Yup.string().required(i18n.t('Form.genericRequired')),
      subjectKey: Yup.string(),
      organizationKey: Yup.string(),
      studyKey: Yup.string(),
      siteKey: Yup.string(),
      deviceId: Yup.string(),
      os: Yup.string(),
      osVersion: Yup.string(),
      phoneModel: Yup.string(),
      phoneManufacturer: Yup.string(),
      bundleId: Yup.string(),
      bundleVersion: Yup.string(),
      timezone: Yup.string(),
      state: Yup.number(),
      appName: Yup.string(),
      uniqueId: Yup.string(),
      createdBy: Yup.string(),
      createdOn: Yup.string(),
      updatedBy: Yup.string(),
      updatedOn: Yup.string(),
      updateReason: Yup.string(),
    },
    initialValues: {
      ...mapDeviceToFormValues(device),
    },
  };

  const handleSubmit = async (values: Record<string, any>) => {
    // TODO: CSP-2739 - Placeholder for device blocking logic.
    // onSave is required by DynamicForm, but no update functionality exists for devices yet.

    return values;
  };

  return (
    <VStack flexGrow={1} gap={'$4'} testID="device-details">
      <Card>
        <DynamicForm
          id="device-details"
          title={i18n.t('Page_Device.title')}
          formConfig={formConfig}
          onSave={handleSubmit}
          maxRows={Math.ceil(formConfig.fields.length / 2)}
          isReadonly={true}
        />
      </Card>
    </VStack>
  );
}
